<template>
  <OnClickOutside :do="close">
    <div
      class="base-select"
      :class="{ 'is-active': isOpen }"
    >
      <button
        ref="button"
        class="base-select__input"
        type="button"
        :disabled="disabled"
        @click="isOpen ? close() : open()"
        @keydown="handleKeydown"
        @keydown.esc="close"
        @keydown.up="highlightPrev"
        @keydown.down="highlightNext"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.tab="close()"
      >
        <span
          v-if="value"
          class="base-select__value"
        >
          <slot
            name="value"
            :value="value"
          >{{ value }}</slot>
        </span>
        <span
          v-else
          class="base-select__placeholder"
        >{{ $t?.(placeholder, l10n_options) || placeholder }}</span>
        <span class="base-select__arrow-wrapper">
          <span class="base-select__arrow">
            <svg
              class="b-icon b-icon-chevron-down"
              width="16"
              height="16"
            >
              <use xlink:href="/static/_v1/pd/svg-sprites/global.svg#icon-chevron-down" />
            </svg>
          </span>
        </span>
      </button>
      <div
        v-show="isOpen && !disabled"
        ref="dropdown"
        class="base-select__dropdown"
      >
        <input
          v-if="filterFunction"
          ref="search"
          v-model="search"
          class="base-select__search"
          :placeholder="$t?.('Найти...', l10n_options) || 'Найти...'"
          @keydown="handleKeydown"
          @keydown.esc="close"
          @keydown.up="highlightPrev"
          @keydown.down="highlightNext"
          @keydown.enter.prevent="selectHighlighted"
          @keydown.tab.prevent
        >
        <div class="base-select__scroller">
          <ul
            v-show="filteredOptions.length > 0"
            ref="options"
            class="base-select__options"
          >
            <slot
              name="item"
              :options="options"
              :select="select"
              :filtered-options="filteredOptions"
              :value="value"
              :highlighted-index="highlightedIndex"
            >
              <li
                v-for="(option, i) in filteredOptions"
                :key="i"
                class="base-select__option"
                :class="{
                  'is-active': i === highlightedIndex,
                  'is-selected': value === option,
                }"
                @click="select(option)"
              >
                {{ option }}
              </li>
            </slot>
          </ul>
          <div
            v-show="filteredOptions.length === 0"
            class="base-select__empty"
          >
            {{ $t?.('Совпадений не найдено', l10n_options) || 'Совпадений не найдено' }}
            <span
              v-if="search.length"
              class="base-select__search-text"
            >
              {{ $t?.('для', l10n_options) || 'для' }} "<b>{{ search }}</b>"</span>
          </div>
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>

<script>
import Popper from 'popper.js'
import OnClickOutside from './OnClickOutside.vue'

export default {
  name: 'BaseSelect',
  components: {
    OnClickOutside,
  },
  props: {
    value: {
      type: [String, Object, Number],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    filterFunction: {
      type: Function,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Выберите значение',
    },
    emptyPlaceholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      search: '',
      highlightedIndex: -1,
      l10n_options: { ns: this.$i18next?.language },
    }
  },
  computed: {
    /**
     * Фильтрует с помощью переданной в props функцией
     * или возвращает список опций без изменений
     * @returns {Array} -
     */
    filteredOptions() {
      const options = []

      if (this.emptyPlaceholder) {
        options.push({ name: this.emptyPlaceholder, value: '' })
      }

      options.push(...this.options)

      if (this.filterFunction) {
        return this.filterFunction(this.search, options)
      }

      return options
    },
  },
  async created() {
    try {
      const userLanguage = this.$i18next?.language

      if (userLanguage) {
        const translation = await import(`www/vue_components/common/locales/${userLanguage}.trans`)

        this.$i18next?.addResourceBundle(userLanguage, userLanguage, translation.default)
      }
    } catch { /* empty */ }
  },

  /**
   * Уничтожает инстанс popper.js
   */
  beforeDestroy() {
    this.popper.destroy()
  },
  methods: {
    /**
     * Открывает dropdown
     * выбирает в качество фокуса строку поиска или кнопку
     */
    open() {
      if (this.isOpen) {
        return
      }

      this.isOpen = true

      this.$nextTick(() => {
        this.setupPopper()

        if (this.filterFunction) {
          this.$refs.search.focus()
        } else {
          this.$refs.button.focus()
        }

        this.scrollToHighlighted()
      })
    },

    /**
     * Обновляет dropdown
     */
    handleKeydown() {
      if (this.popper) {
        this.popper.scheduleUpdate()
      }
    },

    /**
     * Инициализирует popper.js и сохраняет на него ссылку
     */
    setupPopper() {
      if (this.popper === undefined) {
        this.popper = new Popper(this.$refs.button, this.$refs.dropdown, {
          placement: 'bottom',
        })
      } else {
        this.popper.scheduleUpdate()
      }
    },

    /**
     * Закрывает dropdown и выбирает в качестве фокуса кнопку
     */
    close() {
      if (!this.isOpen) {
        return
      }

      this.highlightedIndex = -1
      this.isOpen = false
      this.$refs.button.focus()
    },

    /**
     * Вызывает событие с переданной в нем опцией
     * очищает строку поиска
     * и закрывает dropdown
     * @param {any} option -
     */
    select(option) {
      this.$emit('input', option)
      this.search = ''
      this.highlightedIndex = -1
      this.close()
    },

    /**
     * Выбирает выделенную опцию
     */
    selectHighlighted() {
      this.select(this.filteredOptions[this.highlightedIndex])
    },

    /**
     * Скроллит к выделенному свойству при перемещении по dropdown
     */
    scrollToHighlighted() {
      const children = this.$refs.options?.children[this.highlightedIndex]

      if (!children) {
        return
      }

      children.scrollIntoView({
        block: 'nearest',
      })
    },

    /**
     * Выделяет опцию по индексу
     * @param {any} index -
     */
    highlight(index) {
      this.highlightedIndex = index

      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.filteredOptions.length - 1
      }

      if (this.highlightedIndex > this.filteredOptions.length - 1) {
        this.highlightedIndex = 0
      }

      this.scrollToHighlighted()
    },

    /**
     * Выделяет предыдущую опцию
     */
    highlightPrev() {
      this.highlight(this.highlightedIndex - 1)
    },

    /**
     * Выделяет следующую опцию
     */
    highlightNext() {
      this.highlight(this.highlightedIndex + 1)
    },
  },
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/variables';

.base-select {
  font-size: $font-size-body-1;
  font-family: $font-family-regular;
  position: relative;
  color: #5A5C61;
  max-width: 360px;
}

.base-select__input {
  background-color: $ui-kit-bg-gray-0;
  min-height: 48px;
  border: 1px solid #C7D3D9;
  border-radius: 5px;
  display: flex;
  align-items: center;
  padding: 0;
  cursor: pointer;
  font-size: $font-size-body-1;
  font-family: $font-family-regular;
  width: 100%;
  transition: 0.2s ease-in-out;
}

.base-select__input:hover {
  background-color: $ui-kit-bg-gray-0;
  border-color: #424B58;
}

.base-select__input:focus {
  outline: none;
  border-color: #5A5C61;
}

.base-select__input:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.base-select__value {
  padding: 0 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 350px;
}

.base-select__search {
  color: #5A5C61;
  height: 46px;
  padding: 0 24px;
  margin-top: -1px;
  border: 0;
  border-bottom: 1px solid #C7D3D9;
  border-radius: 5px 5px 0 0;
  font-size: $font-size-body-1;
  width: calc(100% - 48px);
  font-family: $font-family-regular;
}

.base-select__search::placeholder {
  font-size: $font-size-body-1;
  color: #C7D3D9;
}

.base-select__search-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.base-select__search:focus {
  outline: 0;
}

.base-select__placeholder {
  padding: 0 16px;
  white-space: nowrap;
  overflow: hidden;
  color: #424B58;
  text-overflow: ellipsis;
}

.base-select__arrow-wrapper {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-left: auto;
}

.base-select__scroller {
  max-height: 250px;
  margin: 6px 0;
}

.base-select__arrow {
  line-height: 0;
  transition: 0.2s ease-in-out;
  color: #424B58;
}

.base-select__dropdown {
  border: 1px solid #C7D3D9;
  margin-top: 10px;
  box-shadow: 8px 12px 20px rgba(151, 158, 167, 0.2);
  background-color: $ui-kit-bg-gray-0;
  overflow-x: hidden;
  z-index: 100;
  width: 100%;
  border-radius: 5px;
}

.base-select__options {
  margin: 0;
  list-style-type: none;
  padding: 0 6px;
}

.base-select__option {
  display: flex;
  align-items: center;
  word-break: break-word;
  cursor: pointer;
  padding: 20px 10px 20px 16px;
  margin-right: 6px;
  transition: 0.2s ease-in-out;
  border-radius: 5px;
}

.base-select__option.is-active {
  background: #F4F6F7;
  border-radius: 5px;
  color: #181D21;
  font-weight: $weight-medium;
}

.base-select__option.is-selected {
  color: rgba(151, 158, 167, 0.5);
}

.base-select__option:hover {
  background: #F4F6F7;
}

.base-select__empty {
  padding: 0 22px;
  height: 48px;
  line-height: 48px;
  max-width: 350px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.base-select.is-active .base-select__arrow {
  transform: rotate(180deg);
}
</style>
